import axios from "axios";
import {
  afipBackendConfigurado,
  afipCuitEndpoint,
  afipEstadoEndpoint,
} from "./endpoints";
import { cuitValido, limpiarCuit } from "../utils/cuit";
import { resumirConstanciaCruda } from "../utils/arcaResumen";
import { getClientDataByCuit } from "./post";
import supabase from "../supabase";

const TIMEOUT = 60000; // ARCA suele tardar; el ticket WSAA se cachea 12 h del lado del backend.

export class AfipError extends Error {
  /** backendCaido: el problema es el backend, no el CUIT -> conviene el fallback. */
  constructor(message, { sinServidor = false, backendCaido = false } = {}) {
    super(message);
    this.name = "AfipError";
    this.sinServidor = sinServidor;
    this.backendCaido = backendCaido || sinServidor;
  }
}

// El endpoint devuelve datos fiscales de terceros: viaja el token de la sesion
// de Supabase y el backend lo valida antes de consultar a ARCA.
const cabeceras = async () => {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch (error) {
    return {};
  }
};

const errorDeRed = (error) => {
  if (error.response) {
    const detalle = error.response.data?.error;
    // 400 es culpa del CUIT que se mando; el resto es culpa del backend.
    return new AfipError(
      detalle || `El servicio de consulta de CUIT respondió ${error.response.status}.`,
      { backendCaido: error.response.status !== 400 }
    );
  }
  return new AfipError(
    "No pude contactar el servicio de consulta de CUIT.",
    { sinServidor: true }
  );
};

/** Consulta al backend propio (api/afip-cuit.php), que pega directo a ARCA. */
const consultarBackend = async (cuit) => {
  try {
    const { data } = await axios.get(afipCuitEndpoint(cuit), {
      timeout: TIMEOUT,
      headers: await cabeceras(),
    });

    // Sin constancia ni padron no hay nada que autocompletar. Puede ser que el
    // CUIT no exista o que al backend le falte el certificado: no se distingue
    // desde aca, asi que se marca como caido y decide el fallback.
    if (!data?.arca && !data?.padron) {
      throw new AfipError(data?.arca_error || "ARCA no devolvió datos para ese CUIT.", {
        backendCaido: true,
      });
    }

    return { ...data, fuente: "backend" };
  } catch (error) {
    if (error instanceof AfipError) throw error;
    throw errorDeRed(error);
  }
};

/**
 * Fallback: tusfacturas.app, que ya se usa para validar el CUIT al guardar.
 *
 * Devuelve la constancia cruda en `constancia_full_datos`, asi que se la pasa
 * por el mismo resumen y queda igual que lo que devuelve el backend. Sale del
 * navegador, no necesita certificado ni nada instalado en el servidor.
 */
const consultarTusfacturas = async (cuit) => {
  let respuesta;
  try {
    respuesta = await getClientDataByCuit({
      documento_tipo: "CUIT",
      documento_nro: Number(cuit),
    });
  } catch (error) {
    throw new AfipError(
      "No pude consultar el CUIT: ni el servicio propio ni tusfacturas.app respondieron."
    );
  }

  if (respuesta?.error === "S") {
    throw new AfipError(respuesta?.errores?.[0] || "ARCA no devolvió datos para ese CUIT.");
  }

  const cruda = respuesta?.constancia_full_datos;
  if (!cruda?.datosGenerales) {
    throw new AfipError("tusfacturas.app no devolvió la constancia de ese CUIT.");
  }

  return {
    cuit,
    arca: resumirConstanciaCruda(cruda),
    arca_error: null,
    padron: null,
    fuente: "tusfacturas",
  };
};

/**
 * Constancia de inscripcion de un CUIT:
 * { cuit, arca, arca_error, padron, fuente }.
 *
 * Fuente principal: tusfacturas.app, que no necesita nada instalado en el
 * servidor. Si ademas se configuro el backend propio (REACT_APP_AFIP_API_URL),
 * se usa como respaldo cuando tusfacturas no contesta.
 */
export const consultarCuit = async (cuit) => {
  const limpio = limpiarCuit(cuit);

  if (!cuitValido(limpio)) {
    throw new AfipError(
      limpio.length === 11
        ? "El dígito verificador del CUIT no cierra."
        : "El CUIT debe tener 11 dígitos."
    );
  }

  let errorPrincipal;
  try {
    return await consultarTusfacturas(limpio);
  } catch (error) {
    if (!afipBackendConfigurado) throw error;
    errorPrincipal = error;
  }

  try {
    return await consultarBackend(limpio);
  } catch (error) {
    // Si fallan las dos, gana el mensaje mas especifico: el del backend suele
    // decir el motivo real ("La clave se encuentra inactiva").
    throw error.backendCaido ? errorPrincipal : error;
  }
};

/** Que fuentes tiene disponibles el backend: { arca, padron, entorno }. */
export const getEstadoAfip = async () => {
  if (!afipBackendConfigurado) {
    return { arca: false, padron: false, sinBackend: true };
  }
  try {
    const { data } = await axios.get(afipEstadoEndpoint, {
      timeout: 10000,
      headers: await cabeceras(),
    });
    return data;
  } catch (error) {
    return { arca: false, padron: false, sinServidor: true };
  }
};
